import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import MovieCard from '@/components/feature/MovieCard';
import { bannerSlides, movies, quickLinks, categories } from '@/mocks/movies';

function HomeSchema() {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '91精品影视',
    url: 'https://www.91jpys.com/',
    description: '91精品影视免费在线观看平台，提供高清电影、电视剧、综艺、动漫在线观看。',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://www.91jpys.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

export default function Home() {
  const [currentSlide, setCurrentSlide] = useState(0);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % bannerSlides.length);
  }, []);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + bannerSlides.length) % bannerSlides.length);
  }, []);

  useEffect(() => {
    const timer = setInterval(nextSlide, 5000);
    return () => clearInterval(timer);
  }, [nextSlide]);

  const featuredMovies = movies.filter((m) => m.featured);
  const trendingMovies = movies.filter((m) => m.trending);
  const latestMovies = movies.filter((m) => m.isNew);

  return (
    <>
      <HomeSchema />
      <Navbar />

      <main>
        {/* Hero Banner */}
        <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
          {bannerSlides.map((slide, index) => (
            <div
              key={slide.id}
              className={`absolute inset-0 transition-opacity duration-700 ${
                index === currentSlide ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <img
                src={slide.image}
                alt={slide.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50"></div>
            </div>
          ))}

          <div className="absolute inset-0 flex items-center">
            <div className="w-full px-4 md:px-6 mx-auto" style={{ maxWidth: '1400px' }}>
              <div className="max-w-2xl">
                <span className="inline-block px-3 py-1 rounded-full bg-primary-500/90 text-white text-xs font-medium mb-4 whitespace-nowrap">
                  {bannerSlides[currentSlide].badge}
                </span>
                <h1 className="text-3xl md:text-5xl font-heading font-bold text-white mb-3 leading-tight">
                  {bannerSlides[currentSlide].title}
                </h1>
                <p className="text-lg md:text-xl text-white/90 mb-2 font-heading">
                  {bannerSlides[currentSlide].subtitle}
                </p>
                <p className="text-sm md:text-base text-white/70 mb-6">
                  {bannerSlides[currentSlide].description}
                </p>
                <Link
                  to={bannerSlides[currentSlide].link}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-white font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-play-circle-line text-lg"></i>
                  立即观看
                </Link>
              </div>
            </div>
          </div>

          <button
            onClick={prevSlide}
            className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 text-white transition-colors cursor-pointer"
            aria-label="上一张"
          >
            <i className="ri-arrow-left-s-line text-lg md:text-xl"></i>
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 text-white transition-colors cursor-pointer"
            aria-label="下一张"
          >
            <i className="ri-arrow-right-s-line text-lg md:text-xl"></i>
          </button>

          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2">
            {bannerSlides.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`w-2 h-2 rounded-full transition-all duration-300 cursor-pointer ${
                  index === currentSlide ? 'w-6 bg-primary-500' : 'bg-white/50'
                }`}
                aria-label={`第 ${index + 1} 张`}
              />
            ))}
          </div>
        </section>

        {/* Quick Links */}
        <section className="px-4 md:px-6 py-8 md:py-10 mx-auto" style={{ maxWidth: '1400px' }}>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 md:gap-4">
            {quickLinks.map((link) => (
              <Link
                key={link.slug}
                to={`/category/${link.slug}`}
                className="flex flex-col items-center gap-2 p-4 md:p-5 rounded-xl bg-gradient-to-br border border-background-200/60 hover:border-primary-200 transition-all duration-300 group cursor-pointer"
                style={{ backgroundImage: `linear-gradient(135deg, oklch(var(--background-100)), oklch(var(--background-50)))` }}
              >
                <div className={`w-12 h-12 md:w-14 md:h-14 flex items-center justify-center rounded-xl bg-gradient-to-br ${link.gradient} group-hover:scale-110 transition-transform duration-300`}>
                  <i className={`${link.icon} text-xl md:text-2xl text-primary-600`}></i>
                </div>
                <span className="text-sm font-medium text-foreground-700 group-hover:text-primary-600 transition-colors whitespace-nowrap">
                  {link.name}
                </span>
              </Link>
            ))}
          </div>
        </section>

        {/* Featured Section */}
        <section className="px-4 md:px-6 py-10 md:py-14 bg-background-100/70 mx-auto" style={{ maxWidth: '1400px' }}>
          <div className="flex items-center justify-between mb-6 md:mb-8">
            <div>
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">
                精选推荐
              </h2>
              <p className="text-sm text-foreground-400 mt-1">编辑精选，品质保证</p>
            </div>
            <Link
              to="/category/movie"
              className="flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              查看更多 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
            {featuredMovies.map((movie) => (
              <MovieCard
                key={movie.id}
                id={movie.id}
                title={movie.title}
                coverImage={movie.coverImage}
                rating={movie.rating}
                year={movie.year}
                subcategory={movie.subcategory}
                category={movie.category}
                badge="精选"
              />
            ))}
          </div>
        </section>

        {/* Trending Section */}
        <section className="px-4 md:px-6 py-10 md:py-14 mx-auto" style={{ maxWidth: '1400px' }}>
          <div className="flex items-center justify-between mb-6 md:mb-8">
            <div>
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">
                热门推荐
              </h2>
              <p className="text-sm text-foreground-400 mt-1">全网热播，大家都在看</p>
            </div>
            <Link
              to="/category/tv"
              className="flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              查看更多 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
            {trendingMovies.map((movie) => (
              <MovieCard
                key={movie.id}
                id={movie.id}
                title={movie.title}
                coverImage={movie.coverImage}
                rating={movie.rating}
                year={movie.year}
                subcategory={movie.subcategory}
                category={movie.category}
                badge="热门"
              />
            ))}
          </div>
        </section>

        {/* Categories Section */}
        <section className="px-4 md:px-6 py-10 md:py-14 bg-background-100/70 mx-auto" style={{ maxWidth: '1400px' }}>
          <div className="mb-6 md:mb-8">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">
              浏览分类
            </h2>
            <p className="text-sm text-foreground-400 mt-1">按类型发现更多精彩内容</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.slug}`}
                className="group flex items-center gap-4 p-5 rounded-xl bg-background-50 border border-background-200/60 hover:border-primary-200 transition-all duration-300 cursor-pointer"
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 shrink-0 group-hover:bg-primary-500 group-hover:text-white transition-colors duration-300">
                  <i className={`${cat.icon} text-xl`}></i>
                </div>
                <div className="min-w-0">
                  <h3 className="font-medium text-foreground-800 group-hover:text-primary-600 transition-colors text-sm whitespace-nowrap">
                    {cat.name}
                  </h3>
                  <p className="text-xs text-foreground-400 mt-1 truncate">
                    {cat.subcategories.join(' · ')}
                  </p>
                </div>
                <i className="ri-arrow-right-s-line text-foreground-300 ml-auto shrink-0"></i>
              </Link>
            ))}
          </div>
        </section>

        {/* Latest Section */}
        <section className="px-4 md:px-6 py-10 md:py-14 mx-auto" style={{ maxWidth: '1400px' }}>
          <div className="flex items-center justify-between mb-6 md:mb-8">
            <div>
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">
                最新上线
              </h2>
              <p className="text-sm text-foreground-400 mt-1">新鲜出炉，先睹为快</p>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
            {latestMovies.map((movie) => (
              <MovieCard
                key={movie.id}
                id={movie.id}
                title={movie.title}
                coverImage={movie.coverImage}
                rating={movie.rating}
                year={movie.year}
                subcategory={movie.subcategory}
                category={movie.category}
                badge="新上线"
              />
            ))}
          </div>
        </section>

        {/* FAQ Section */}
        <section className="px-4 md:px-6 py-10 md:py-14 bg-background-100/70 mx-auto" style={{ maxWidth: '1400px' }}>
          <div className="max-w-3xl mx-auto">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 text-center mb-8">
              常见问题
            </h2>
            <div className="space-y-3">
              {[
                { q: '91精品影视需要收费吗？', a: '91精品影视是一个完全免费的影视内容信息平台，无需注册，无需付费，即可浏览所有影视内容信息。' },
                { q: '如何查找想看的影片？', a: '您可以通过首页的分类导航、搜索功能快速找到感兴趣的影片。我们提供电影、电视剧、综艺、动漫、纪录片等多种分类，方便您浏览。' },
                { q: '内容更新频率如何？', a: '我们与各大平台保持同步更新，新上映的影片会第一时间同步到平台，确保您不错过任何精彩内容。' },
              ].map((faq, index) => (
                <details key={index} className="group rounded-xl bg-background-50 border border-background-200/60 overflow-hidden">
                  <summary className="flex items-center justify-between px-5 py-4 cursor-pointer list-none">
                    <span className="text-sm font-medium text-foreground-800 pr-4">{faq.q}</span>
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform duration-200 shrink-0"></i>
                  </summary>
                  <div className="px-5 pb-4">
                    <p className="text-sm text-foreground-500 leading-relaxed">{faq.a}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>

          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'FAQPage',
                mainEntity: [
                  { '@type': 'Question', name: '91精品影视需要收费吗？', acceptedAnswer: { '@type': 'Answer', text: '91精品影视是一个完全免费的影视内容信息平台，无需注册，无需付费，即可浏览所有影视内容信息。' } },
                  { '@type': 'Question', name: '如何查找想看的影片？', acceptedAnswer: { '@type': 'Answer', text: '您可以通过首页的分类导航、搜索功能快速找到感兴趣的影片。我们提供电影、电视剧、综艺、动漫、纪录片等多种分类，方便您浏览。' } },
                  { '@type': 'Question', name: '内容更新频率如何？', acceptedAnswer: { '@type': 'Answer', text: '我们与各大平台保持同步更新，新上映的影片会第一时间同步到平台，确保您不错过任何精彩内容。' } },
                ],
              }),
            }}
          />
        </section>

        {/* CTA Section */}
        <section className="relative px-4 md:px-6 py-16 md:py-20 overflow-hidden mx-auto" style={{ maxWidth: '1400px' }}>
          <div className="absolute inset-0 rounded-2xl overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Dark%20cinematic%20home%20theater%20setup%20with%20warm%20ambient%20lighting%2C%20large%20screen%20with%20subtle%20glow%2C%20cozy%20atmosphere%2C%20luxury%20living%20room%2C%20dark%20and%20warm%20tones%2C%20cinematic%20photography%2C%20rich%20textures&width=1400&height=400&seq=cta-bg-home&orientation=landscape"
              alt="影视体验"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-black/40"></div>
          </div>
          <div className="relative z-10 max-w-xl">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-white mb-3">
              开启你的观影之旅
            </h2>
            <p className="text-white/70 text-sm md:text-base mb-6 leading-relaxed">
              海量高清影视内容，随时随地免费浏览。加入数千万用户的观影社区，发现更多精彩。
            </p>
            <Link
              to="/category/movie"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-white font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              立即探索 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </section>
      </main>

      <Footer />
      <ScrollToTop />
    </>
  );
}